import {
  ErrorResponse,
  UserManager,
  WebStorageStateStore,
  type User,
  type UserManagerSettings,
} from "oidc-client-ts";

import {
  PREVIEW_SIGNIN_CALLBACK_PATH,
  buildDirectCognitoLogoutUrl,
  buildPreviewLogoutStartUrl,
  createPreviewRouteHint,
  parsePreviewRelayConfig,
  type PreviewRelayConfig,
} from "../auth/previewRelay";
import type { AuthState } from "../auth/AuthContext";
import {
  authCapabilities,
  identityFromProfile,
  recognizedRolesFromAccessToken,
} from "../auth/roles";
import { configureTemporalUiBase } from "./runtimeConfig";

type Listener = () => void;

export type RuntimeAppConfig = {
  enabled: boolean;
  authority: string;
  clientId: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  scope: string;
  temporalUiBase: string;
  previewRelay: PreviewRelayConfig | null;
};

const DEFAULT_SCOPE = "openid email profile";
const INVALID_REFRESH_GRANT = "invalid_grant";
const listeners = new Set<Listener>();

export type AuthSessionManager = Pick<
  UserManager,
  "getUser" | "removeUser" | "signinSilent" | "storeUser"
>;

/** Owns the persisted OIDC user and refreshes it before callers use a token. */
export class AuthSession {
  private currentUser: User | null = null;
  private generation = 0;
  private renewal: Promise<User | null> | null = null;
  private renewalAccessToken: string | null = null;

  public constructor(private readonly manager: AuthSessionManager) {}

  public hasUsableCredentials(): boolean {
    return isFreshUser(this.currentUser);
  }

  public user(): User | null {
    return this.currentUser;
  }

  public setUser(user: User): void {
    this.currentUser = user;
  }

  public replaceUser(user: User): void {
    this.generation += 1;
    this.currentUser = user;
  }

  public setUserFromManagerEvent(user: User): void {
    if (!this.renewal) {
      this.currentUser = user;
    }
  }

  public clearUser(): void {
    this.currentUser = null;
  }

  public async restore(): Promise<User | null> {
    const generation = this.generation;
    const storedUser = await this.manager.getUser();
    if (generation !== this.generation) {
      await this.preserveCurrentSession();
      return null;
    }
    if (!storedUser) {
      this.currentUser = null;
      return null;
    }
    if (isFreshUser(storedUser)) {
      this.currentUser = storedUser;
      return storedUser;
    }
    if (!storedUser.refresh_token) {
      await this.discard();
      return null;
    }
    return this.renew();
  }

  public renewExpiringUser(): Promise<User | null> {
    if (!this.currentUser?.refresh_token) {
      return Promise.resolve(this.currentUser);
    }
    return this.renew();
  }

  public async authorizationHeader(): Promise<string | null> {
    const currentUser = this.currentUser;
    const user =
      currentUser && isFreshUser(currentUser)
        ? currentUser
        : await this.restore();
    return user ? `Bearer ${user.access_token}` : null;
  }

  public async discard(): Promise<void> {
    this.invalidate();
    await this.manager.removeUser();
  }

  public invalidate(): void {
    this.generation += 1;
    this.currentUser = null;
  }

  public async discardIfCurrentAuthorization(
    authorization: string | null,
  ): Promise<boolean> {
    if (
      this.renewal &&
      this.renewalAccessToken &&
      authorization === `Bearer ${this.renewalAccessToken}`
    ) {
      return false;
    }
    if (
      !authorization ||
      !this.currentUser ||
      authorization !== `Bearer ${this.currentUser.access_token}`
    ) {
      return false;
    }
    await this.discard();
    return true;
  }

  private async renew(): Promise<User | null> {
    if (this.renewal) {
      return this.renewal;
    }
    const generation = this.generation;
    this.renewalAccessToken = this.currentUser?.access_token ?? null;
    const renewal = this.renewOnce(generation);
    this.renewal = renewal;
    try {
      return await renewal;
    } finally {
      if (this.renewal === renewal) {
        this.renewal = null;
        this.renewalAccessToken = null;
      }
    }
  }

  private async renewOnce(generation: number): Promise<User | null> {
    try {
      const renewedUser = await this.manager.signinSilent();
      if (generation !== this.generation) {
        await this.preserveCurrentSession();
        return null;
      }
      if (isFreshUser(renewedUser)) {
        this.currentUser = renewedUser;
        return renewedUser;
      }
      await this.discard();
      return null;
    } catch (error: unknown) {
      if (generation !== this.generation) {
        await this.preserveCurrentSession();
        return null;
      }
      if (isInvalidRefreshGrant(error)) {
        await this.discard();
        return null;
      }
      throw error;
    }
  }

  private async preserveCurrentSession(): Promise<void> {
    if (this.currentUser) {
      await this.manager.storeUser(this.currentUser);
    } else {
      await this.manager.removeUser();
    }
  }
}

let runtimeConfig: RuntimeAppConfig = {
  enabled: false,
  authority: "",
  clientId: "",
  redirectUri: defaultRedirectUri(),
  postLogoutRedirectUri: defaultPostLogoutRedirectUri(),
  scope: DEFAULT_SCOPE,
  temporalUiBase: import.meta.env.VITE_TEMPORAL_UI_BASE || "",
  previewRelay: null,
};
let manager: UserManager | null = null;
let session: AuthSession | null = null;
let initialization: Promise<void> | null = null;

/** Load runtime Cognito config and the stored OIDC user once before rendering. */
export function initializeAuth(): Promise<void> {
  if (!initialization) {
    initialization = initializeAuthOnce().catch((error: unknown) => {
      initialization = null;
      manager = null;
      session = null;
      throw error;
    });
  }
  return initialization;
}

async function initializeAuthOnce(): Promise<void> {
  runtimeConfig = await loadRuntimeConfig();
  configureTemporalUiBase(runtimeConfig.temporalUiBase);
  if (!runtimeConfig.enabled) {
    emit();
    return;
  }
  if (!runtimeConfig.authority || !runtimeConfig.clientId) {
    throw new Error(
      "Cognito auth is enabled but authority/client id is missing",
    );
  }

  const nextManager = new UserManager(userManagerSettings(runtimeConfig));
  const nextSession = new AuthSession(nextManager);
  const emitIfCurrent = () => {
    if (session === nextSession) {
      emit();
    }
  };
  const unsubscribe = [
    nextManager.events.addUserLoaded((user) => {
      nextSession.setUserFromManagerEvent(user);
      emitIfCurrent();
    }),
    nextManager.events.addUserUnloaded(() => {
      nextSession.clearUser();
      emitIfCurrent();
    }),
    nextManager.events.addAccessTokenExpired(() => {
      nextSession.clearUser();
      emitIfCurrent();
    }),
  ];

  try {
    await nextSession.restore();
  } catch (error: unknown) {
    for (const stopListening of unsubscribe) {
      stopListening();
    }
    throw error;
  }
  nextManager.events.addAccessTokenExpiring(() => {
    // Keep transient failures retryable: request-time auth uses the same
    // single-flight renewal path when the next API call arrives.
    void nextSession
      .renewExpiringUser()
      .then(emitIfCurrent)
      .catch(() => undefined);
  });
  manager = nextManager;
  session = nextSession;
  emit();
}

function userManagerSettings(config: RuntimeAppConfig): UserManagerSettings {
  const storage = new WebStorageStateStore({ store: window.localStorage });
  const previewRelay = previewRelayForCurrentOrigin(config);
  return {
    authority: config.authority,
    client_id: config.clientId,
    redirect_uri: previewRelay
      ? `${previewRelay.origin}${PREVIEW_SIGNIN_CALLBACK_PATH}`
      : config.redirectUri,
    post_logout_redirect_uri: config.postLogoutRedirectUri,
    response_type: "code",
    scope: config.scope,
    automaticSilentRenew: false,
    stateStore: storage,
    userStore: storage,
  };
}

export async function loadRuntimeConfig(): Promise<RuntimeAppConfig> {
  const envConfig = envRuntimeConfig();
  try {
    const response = await fetch("/auth-config.json", {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) {
      throw new Error(`Auth configuration request failed: ${response.status}`);
    }
    const parsed: unknown = await response.json();
    validateRuntimeConfigDocument(parsed);
    return parseRuntimeConfig(parsed, envConfig);
  } catch (error: unknown) {
    // Deployed images always write this file. Treat its absence as an auth
    // outage, never as permission to enter the auth-disabled admin posture.
    if (import.meta.env.PROD) {
      throw error instanceof Error
        ? error
        : new Error("Auth configuration could not be loaded");
    }
    return envConfig;
  }
}

/**
 * The deployed file is an authorization control, not an optional preference.
 * Reject a syntactically valid but incomplete document before flexible
 * fallback parsing could turn it into the auth-disabled local posture.
 */
export function validateRuntimeConfigDocument(value: unknown): void {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Auth configuration is malformed");
  }
  const record = value as Record<string, unknown>;
  if (typeof record.enabled !== "boolean") {
    throw new Error("Auth configuration must declare enabled as a boolean");
  }
  if (
    record.enabled &&
    (!stringValue(record.authority) || !stringValue(record.clientId))
  ) {
    throw new Error(
      "Enabled auth configuration must declare authority and clientId",
    );
  }
  if (record.previewRelay !== undefined) {
    parsePreviewRelayConfig(record.previewRelay);
  }
}

function envRuntimeConfig(): RuntimeAppConfig {
  const previewRelay = parsePreviewRelayConfig({
    enabled: import.meta.env.VITE_PREVIEW_AUTH_RELAY_ENABLED === "true",
    origin: import.meta.env.VITE_PREVIEW_AUTH_RELAY_ORIGIN || "",
    allowedHostPrefix:
      import.meta.env.VITE_PREVIEW_AUTH_ALLOWED_HOST_PREFIX || "",
    allowedHostSuffix:
      import.meta.env.VITE_PREVIEW_AUTH_ALLOWED_HOST_SUFFIX || "",
    logoutEndpoint: import.meta.env.VITE_COGNITO_LOGOUT_ENDPOINT || "",
  });
  return {
    enabled: import.meta.env.VITE_COGNITO_ENABLED === "true",
    authority: import.meta.env.VITE_COGNITO_AUTHORITY || "",
    clientId: import.meta.env.VITE_COGNITO_CLIENT_ID || "",
    redirectUri:
      import.meta.env.VITE_COGNITO_REDIRECT_URI || defaultRedirectUri(),
    postLogoutRedirectUri:
      import.meta.env.VITE_COGNITO_POST_LOGOUT_REDIRECT_URI ||
      defaultPostLogoutRedirectUri(),
    scope: import.meta.env.VITE_COGNITO_SCOPE || DEFAULT_SCOPE,
    temporalUiBase: import.meta.env.VITE_TEMPORAL_UI_BASE || "",
    previewRelay,
  };
}

export function parseRuntimeConfig(
  value: unknown,
  fallback: RuntimeAppConfig,
): RuntimeAppConfig {
  if (!value || typeof value !== "object") return fallback;
  const record = value as Record<string, unknown>;
  const authority = stringValue(record.authority) || fallback.authority;
  const clientId = stringValue(record.clientId) || fallback.clientId;
  const previewRelay =
    record.previewRelay === undefined
      ? fallback.previewRelay
      : parsePreviewRelayConfig(record.previewRelay);
  return {
    enabled: booleanValue(record.enabled) ?? fallback.enabled,
    authority,
    clientId,
    redirectUri:
      stringValue(record.redirectUri) ||
      fallback.redirectUri ||
      defaultRedirectUri(),
    postLogoutRedirectUri:
      stringValue(record.postLogoutRedirectUri) ||
      fallback.postLogoutRedirectUri ||
      defaultPostLogoutRedirectUri(),
    scope: stringValue(record.scope) || fallback.scope || DEFAULT_SCOPE,
    temporalUiBase:
      stringValue(record.temporalUiBase) || fallback.temporalUiBase,
    previewRelay,
  };
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function booleanValue(value: unknown): boolean | null {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    if (value.toLowerCase() === "true") return true;
    if (value.toLowerCase() === "false") return false;
  }
  return null;
}

function defaultRedirectUri(): string {
  return `${browserOrigin()}/auth/callback`;
}

function defaultPostLogoutRedirectUri(): string {
  return browserOrigin();
}

function browserOrigin(): string {
  return typeof window === "undefined"
    ? "http://localhost"
    : window.location.origin;
}

function isFreshUser(user: User | null): boolean {
  return !!user && !!user.access_token && !user.expired;
}

function isInvalidRefreshGrant(error: unknown): boolean {
  return (
    error instanceof ErrorResponse && error.error === INVALID_REFRESH_GRANT
  );
}

function requireManager(): UserManager {
  if (!manager) throw new Error("Cognito auth has not been initialized");
  return manager;
}

export function isAuthEnabled(): boolean {
  return runtimeConfig.enabled;
}

/** True when requests can proceed: auth disabled, or a fresh OIDC user exists. */
export function hasUsableCredentials(): boolean {
  if (!runtimeConfig.enabled) return true;
  return session?.hasUsableCredentials() ?? false;
}

/** Current typed identity and effective workspace permissions. */
export function authState(): AuthState {
  if (!runtimeConfig.enabled) {
    return {
      configured: false,
      authenticated: true,
      identity: { subject: "local-development", displayName: "Local admin" },
      roles: ["admin"],
      ...authCapabilities(false, []),
    };
  }
  const user = session?.user() ?? null;
  const authenticated = isFreshUser(user);
  const tokenRoles = authenticated
    ? recognizedRolesFromAccessToken(user?.access_token ?? null)
    : [];
  const identity = authenticated ? identityFromProfile(user?.profile) : null;
  const roles = identity ? tokenRoles : [];
  return {
    configured: true,
    authenticated,
    identity,
    roles,
    ...authCapabilities(true, roles),
  };
}

/** `Authorization` header value for API requests, if Cognito auth is active. */
export async function authHeader(): Promise<string | null> {
  if (!runtimeConfig.enabled) {
    return null;
  }
  const wasUsable = session?.hasUsableCredentials() ?? false;
  const header = (await session?.authorizationHeader()) ?? null;
  if (!wasUsable && session?.hasUsableCredentials()) {
    emit();
  }
  return header;
}

/** Start the hosted-UI Authorization Code + PKCE sign-in flow. */
export async function signIn(returnTo: string): Promise<void> {
  await initializeAuth();
  const previewRelay = previewRelayForCurrentOrigin(runtimeConfig);
  await requireManager().signinRedirect({
    state: { returnTo },
    ...(previewRelay
      ? {
          url_state: createPreviewRouteHint(
            window.location.origin,
            previewRelay,
          ),
        }
      : {}),
  });
}

/** Finish the hosted-UI redirect and return the same-origin route to restore. */
export async function completeSignIn(callbackUrl: string): Promise<string> {
  await initializeAuth();
  const currentManager = requireManager();
  const user = await currentManager.signinRedirectCallback(callbackUrl);
  session?.replaceUser(user);
  await currentManager.storeUser(user);
  emit();
  return returnToFromState(user.state);
}

function returnToFromState(state: unknown): string {
  if (!state || typeof state !== "object") return "/";
  const returnTo = (state as { returnTo?: unknown }).returnTo;
  if (typeof returnTo !== "string") return "/";
  return returnTo.startsWith("/") && !returnTo.startsWith("//")
    ? returnTo
    : "/";
}

/** Clear the local OIDC user without a provider logout redirect. */
function clearCredentials(): void {
  const discard = session?.discard();
  emit();
  if (discard) {
    void discard.finally(emit);
  }
}

/** Clear only when the rejected request used the still-current access token. */
export function clearRejectedCredentials(authorization: string | null): void {
  const discard = session?.discardIfCurrentAuthorization(authorization);
  if (discard) {
    emit();
    void discard.finally(emit);
  }
}

/** Sign out locally and redirect through Cognito logout when configured. */
export async function signOut(): Promise<void> {
  await initializeAuth();
  if (!runtimeConfig.enabled) {
    clearCredentials();
    return;
  }
  const currentManager = requireManager();
  const previewRelay = previewRelayForCurrentOrigin(runtimeConfig);
  session?.invalidate();
  emit();
  if (previewRelay) {
    await currentManager.removeUser();
    window.location.assign(
      buildPreviewLogoutStartUrl(window.location.origin, previewRelay),
    );
    return;
  }
  const logoutEndpoint =
    runtimeConfig.previewRelay?.logoutEndpoint ??
    (await currentManager.metadataService.getEndSessionEndpoint());
  if (!logoutEndpoint) throw new Error("Cognito logout endpoint is missing");
  await currentManager.removeUser();
  window.location.assign(
    buildDirectCognitoLogoutUrl(
      logoutEndpoint,
      runtimeConfig.clientId,
      runtimeConfig.postLogoutRedirectUri,
    ),
  );
}

/** Subscribe to auth changes (set/clear/expiry); returns an unsubscribe fn. */
export function subscribeAuth(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function emit(): void {
  for (const fn of listeners) fn();
}

function previewRelayForCurrentOrigin(
  config: RuntimeAppConfig,
): PreviewRelayConfig | null {
  const previewRelay = config.previewRelay;
  if (!previewRelay || window.location.origin === previewRelay.origin) {
    return null;
  }
  createPreviewRouteHint(window.location.origin, previewRelay);
  return previewRelay;
}
